import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzVpbDataService,
  CorporateTaxEntity,
  OmschrijvingEnBedrag,
  VpbAangifte
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  shareReplay,
  Subscription,
  tap
} from "rxjs";
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-gewone-giften',
  templateUrl: './gewone-giften.component.html',
  styleUrls: ['./gewone-giften.component.scss'],
  imports: [
    SharedUiButtonModule,
    ReactiveFormsModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    CommonModule
  ]
})
export class GewoneGiftenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);
  private bedragSubscriptions = new Subscription();

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      tap(data => {
        if (data.aangifte) {
          this.updateForm(data.aangifte);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public omschrijvingBedragFg = new FormGroup({
    omschrijvingBedrag: new FormArray<
      FormGroup<{
        omschrijving: FormControl<string | null>;
        bedrag: FormControl<number | null>;
      }>
    >([]),
  });

  get omschrijvingBedrag(): FormArray<FormGroup<{ omschrijving: FormControl<string | null>; bedrag: FormControl<number | null>; }>> {
    return this.omschrijvingBedragFg.controls.omschrijvingBedrag;
  }

  addRij(): void {
    this.omschrijvingBedrag.push(this.createFormGroup());
    this.watchBedragChanges();
  }

  verwijderRij(index: number): void {
    this.omschrijvingBedrag.removeAt(index);
    this.saveForm();
    this.dataService.calculateLatestCorporateTaxReturn().subscribe();
    this.watchBedragChanges();
  }

  createFormGroup(item?: OmschrijvingEnBedrag) {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
    });
  }

  ngOnInit(): void {
    this.watchBedragChanges();
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  private watchBedragChanges(): void {
    this.bedragSubscriptions.unsubscribe();
    this.bedragSubscriptions = new Subscription();

    this.omschrijvingBedrag.controls.forEach(group => {
      this.bedragSubscriptions.add(
        group.controls.bedrag.valueChanges.pipe(
          debounceTime(300),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
          this.saveForm();
          this.dataService.calculateLatestCorporateTaxReturn().subscribe();
        })
      );
    });
  }

  private updateForm(vpbAangifte: VpbAangifte): void {
    const lijst = vpbAangifte.vermogensvergelijking?.giftenAanOverigeInstellingen ?? [];
    const formArray = this.omschrijvingBedragFg.controls.omschrijvingBedrag;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, { emitEvent: false });
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], { emitEvent: false });
      } else {
        formArray.push(this.createFormGroup(lijst[i]), { emitEvent: false });
      }
    }
  }

  saveForm(): void {
    this.omschrijvingBedragFg.updateValueAndValidity({ emitEvent: false })

    const specificaties: OmschrijvingEnBedrag[] =
      this.omschrijvingBedragFg.getRawValue().omschrijvingBedrag
        .map(item => ({
          omschrijving: item.omschrijving?.trim()
            ? item.omschrijving
            : undefined,
          bedrag: item.bedrag ?? undefined,
        }))
        .filter(item =>
          item.omschrijving !== undefined || item.bedrag !== undefined
        );

    this.dataService.patchCorporateTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          vermogensvergelijking: {
            ...currentEntity.aangifte.vermogensvergelijking,
            giftenAanOverigeInstellingen: specificaties,
          },
        },
      };
    });
  }
}
